// Career & salary data — Morocco 2026
// Source: JAD2 Advisory Research + HCP Morocco 2026 + AfricaWork 2026 Salary Guide
// + Employsome Morocco 2026 + Xpertize Africa + CNSS declarations
// Salaries: gross monthly MAD. Employment rate: formal-sector, within 12 months.

export interface SchoolCareers {
  jobFamilies: string[];
  avgStartSalaryMAD: number;
  avgMidSalaryMAD: number;
  topEmployers: string[];
  employmentRate: number;
  internationalOpportunities?: boolean;
  keyInsight?: string;
}

export const CAREERS_DATA: Record<string, SchoolCareers> = {

  // ─── TIER 1 — ELITE ENGINEERING (CNC) ────────────────────────────────────
  "emi": {
    jobFamilies: ["Ingénieur logiciel", "Data Engineer", "Chef de projet SI", "Consultant IT", "Architecte systèmes", "DevOps Engineer", "ML Engineer"],
    avgStartSalaryMAD: 11000,
    avgMidSalaryMAD: 32000,
    topEmployers: ["OCP Group", "Maroc Telecom", "CDG", "BMCE Bank", "McKinsey Maroc", "Capgemini Maroc", "Attijariwafa Bank", "Royal Air Maroc"],
    employmentRate: 96,
    internationalOpportunities: true,
    "keyInsight": "La plus ancienne grande école d'ingénieurs du Maroc. Ses diplômés dominent les directions IT des grands groupes publics (OCP, CDG, ONEE) et les cabinets de conseil internationaux. Réseau alumni très actif en Afrique francophone."
  },

  "ehtp": {
    jobFamilies: ["Ingénieur génie civil", "Chef de chantier BTP", "Ingénieur transport", "Directeur de projets", "Urbaniste", "Ingénieur hydraulique", "BIM Manager"],
    avgStartSalaryMAD: 10000,
    avgMidSalaryMAD: 28000,
    topEmployers: ["OCP Group", "ONCF", "ADM (Autoroutes)", "Lydec", "Groupe Holcim", "TGCC", "Al Omrane", "Bouygues Maroc", "EGIS"],
    employmentRate: 95,
    internationalOpportunities: true,
    "keyInsight": "Référence nationale pour les grands travaux publics — autoroutes, ports, TGV. Les diplômés pilotent les plus grands chantiers d'infrastructure du Maroc. Double diplôme avec écoles françaises possible."
  },

  "ensias": {
    jobFamilies: ["Ingénieur logiciel", "Cybersécurité", "Data Scientist", "Architecte cloud", "Product Manager", "AI Engineer", "Blockchain Developer"],
    avgStartSalaryMAD: 12000,
    avgMidSalaryMAD: 35000,
    topEmployers: ["Capgemini Maroc", "Sqli", "Maroc Telecom", "OCP Digital", "CGI", "Oracle Maroc", "IBM", "Google (remote)", "startups tech"],
    employmentRate: 97,
    internationalOpportunities: true,
    "keyInsight": "#1 informatique au Maroc. Les profils IA/ML d'ENSIAS sont les plus recherchés par les multinationales tech. Salaire départ pour profils IA : 12 000–18 000 MAD. Seniors IA : 35 000–60 000 MAD. Forte culture CTF et cybersécurité."
  },

  "inpt": {
    jobFamilies: ["Ingénieur télécoms", "Ingénieur réseaux", "Cybersécurité", "Ingénieur cloud", "IoT Engineer", "5G Specialist", "Network Architect"],
    avgStartSalaryMAD: 11000,
    avgMidSalaryMAD: 30000,
    topEmployers: ["Maroc Telecom", "Orange Maroc", "Inwi", "Nokia", "Ericsson Maroc", "Huawei Maroc", "Cisco", "AWS", "Azure"],
    employmentRate: 96,
    internationalOpportunities: true,
    "keyInsight": "Dominant en télécoms et réseaux. Les diplômés pilotent le déploiement 5G et la fibre optique au Maroc. Partenariats directs avec Nokia, Ericsson, Huawei, Cisco, AWS et Azure."
  },

  "enim": {
    jobFamilies: ["Ingénieur minier", "Ingénieur chimiste", "Géologue de production", "Ingénieur environnement", "Ingénieur procédés", "HSE Manager"],
    avgStartSalaryMAD: 9000,
    avgMidSalaryMAD: 24000,
    topEmployers: ["OCP Group", "MANAGEM Group", "CMH", "COSUMAR", "LafargeHolcim", "MASEN", "ONEE", "Managem"],
    employmentRate: 93,
    internationalOpportunities: false,
    "keyInsight": "L'école de référence pour l'industrie minière et chimique marocaine. OCP Group est l'employeur dominant. Carrières stables mais concentrées géographiquement (Khouribga, Benguerir, Laâyoune)."
  },

  "insea": {
    jobFamilies: ["Actuaire", "Data Scientist", "Économètre", "Analyste risques", "Statisticien", "Quantitative Analyst", "Risk Modeler"],
    avgStartSalaryMAD: 10000,
    avgMidSalaryMAD: 28000,
    topEmployers: ["Bank Al-Maghrib", "Wafa Assurance", "HCP", "CDG", "CIH Bank", "AXA Maroc", "Saham Assurance", "BCP Group"],
    employmentRate: 94,
    internationalOpportunities: true,
    "keyInsight": "Seule école nationale de statistique et d'actuariat au Maroc. Les diplômés sont indispensables aux départements risques des banques et aux équipes pricing des assurances. Les certifications actuarielles (IA) ouvrent des salaires de 40 000+ MAD."
  },


  // ─── TIER 2 — ELITE BUSINESS ──────────────────────────────────────────────
  "iscae": {
    jobFamilies: ["Auditeur financier", "Contrôleur de gestion", "Directeur financier", "Analyste stratégique", "Manager commercial", "Consultant", "DG PME"],
    avgStartSalaryMAD: 9000,
    avgMidSalaryMAD: 26000,
    topEmployers: ["Deloitte Maroc", "KPMG", "PwC", "Attijariwafa Bank", "BMCE Bank", "ONA Group", "CDG", "Crédit du Maroc", "Saham"],
    employmentRate: 94,
    internationalOpportunities: true,
    "keyInsight": "La grande école de commerce publique du Maroc, accréditée AACSB. Les Big 4 (Deloitte, KPMG, PwC, EY) recrutent massivement. Le MBA Executive alimente le pipeline des directeurs généraux et DAF des plus grandes entreprises."
  },


  // ─── UM6P ─────────────────────────────────────────────────────────────────
  "um6p": {
    jobFamilies: ["Data Scientist", "Ingénieur R&D", "Manager de projet international", "Chercheur", "Entrepreneur tech", "Agronome de précision", "Energy Transition Specialist", "Green Finance Analyst"],
    avgStartSalaryMAD: 15000,
    avgMidSalaryMAD: 45000,
    topEmployers: ["OCP Group", "McKinsey", "CERN", "Google", "Startups tech", "MASEN", "IRESEN", "African Development Bank"],
    employmentRate: 92,
    internationalOpportunities: true,
    "keyInsight": "L'université la plus orientée recherche d'Afrique, adossée à OCP. Enseignement 100% anglais. Bourses très généreuses (jusqu'à 100% pour les profils excellence). Les diplômés data/greentech commandent des salaires premium dès la sortie."
  },


  // ─── ENSAM NETWORK ────────────────────────────────────────────────────────
  "ensam-casablanca": {
    jobFamilies: ["Ingénieur industriel", "Responsable qualité", "Ingénieur méthodes", "Ingénieur maintenance", "Responsable production", "Lean Manager"],
    avgStartSalaryMAD: 7500,
    avgMidSalaryMAD: 18000,
    topEmployers: ["PSA Maroc", "Renault Tanger", "Safran", "Yazaki", "Valeo Maroc", "Stellantis", "LEONI", "SOMACA"],
    employmentRate: 91,
    internationalOpportunities: false,
    "keyInsight": "Pipeline direct vers les équipementiers automobiles et aéronautiques (PSA/Stellantis, Renault, Safran, Yazaki). L'ingénierie industrielle et la qualité sont les points forts historiques."
  },

  "ensam-meknes": {
    jobFamilies: ["Ingénieur industriel", "Responsable qualité", "Ingénieur méthodes", "Ingénieur HSE", "Responsable logistique", "Production Manager"],
    avgStartSalaryMAD: 7000,
    avgMidSalaryMAD: 16000,
    topEmployers: ["Stellantis", "Renault Maroc", "SOMACA", "Sews Cabind", "LEAR Corporation", "Fès-Meknès industrial zone"],
    employmentRate: 90,
    internationalOpportunities: false,
  },
  "ensam-rabat": {
    jobFamilies: ["Ingénieur industriel", "Ingénieur méthodes", "Ingénieur électromécanique", "Responsable qualité", "Manufacturing Digital"],
    avgStartSalaryMAD: 7000,
    avgMidSalaryMAD: 16000,
    topEmployers: ["OCP Group", "CDG", "PME industrielle Rabat-Salé", "ONCF", "ONEE", "Secteur automobile"],
    employmentRate: 89,
    internationalOpportunities: false,
  },

  // ─── ENSA NETWORK ─────────────────────────────────────────────────────────
  "ensa-casablanca": {
    jobFamilies: ["Ingénieur logiciel", "Ingénieur génie civil", "Ingénieur systèmes embarqués", "Ingénieur cloud", "Chef de projet", "DevOps", "Data Engineer"],
    avgStartSalaryMAD: 7500,
    avgMidSalaryMAD: 18000,
    topEmployers: ["CGI Maroc", "Capgemini", "Lydec", "OCP", "IBM Maroc", "Sqli", "IT Factory", "startups Casa"],
    employmentRate: 91,
    internationalOpportunities: true,
    "keyInsight": "L'ENSA la plus orientée tech. Forte filière logiciel et cloud computing. La localisation Casablanca donne accès aux multinationales IT en offshoring vers l'Europe."
  },

  "ensa-agadir": {
    jobFamilies: ["Ingénieur génie civil", "Ingénieur électrique", "Développeur logiciel", "Ingénieur agroalimentaire", "Chef de projet", "Ingénieur énergie renouvelable"],
    avgStartSalaryMAD: 6500,
    avgMidSalaryMAD: 15000,
    topEmployers: ["Agro industrie Souss", "ONEE", "Sogemar", "Cosumar", "Bureau d'études", "Masen (Noor)", "OCP Agadir"],
    employmentRate: 88,
    internationalOpportunities: false,
  },
  "ensa-fes": {
    jobFamilies: ["Ingénieur informatique", "Ingénieur mécanique", "Ingénieur électrique", "Chef de projet industrie", "Ingénieur qualité", "Développeur embarqué"],
    avgStartSalaryMAD: 6500,
    avgMidSalaryMAD: 15000,
    topEmployers: ["SNEP", "Groupe Léon Grosse", "ONEEP", "Safran", "Yazaki", "Fès-Meknès industrial zone", "PME tech Fès"],
    employmentRate: 88,
    internationalOpportunities: false,
  },
  "ensa-marrakech": {
    jobFamilies: ["Ingénieur génie civil", "Ingénieur électrique", "Développeur web", "Ingénieur BI", "Chef de projet construction", "Tourism tech"],
    avgStartSalaryMAD: 6500,
    avgMidSalaryMAD: 14000,
    topEmployers: ["Groupe Alliances", "BMCE Bank", "OCP", "ONEE", "Bureau d'études Marrakech", "PME construction", "ONMT"],
    employmentRate: 87,
    internationalOpportunities: false,
  },
  "ensa-kenitra": {
    jobFamilies: ["Ingénieur automobile", "Ingénieur informatique", "Ingénieur production", "Technicien chef de produit", "Ingénieur qualité", "Supply Chain"],
    avgStartSalaryMAD: 6500,
    avgMidSalaryMAD: 15000,
    topEmployers: ["PSA Kénitra", "Renault Maroc", "SNEP", "LEONI", "Safran Maroc", "Stellantis Kénitra", "automotive suppliers"],
    employmentRate: 89,
    internationalOpportunities: false,
  },
  "ensa-tanger": {
    jobFamilies: ["Ingénieur logistique", "Ingénieur portuaire", "Développeur logiciel", "Ingénieur transport", "Chef de projet douane", "Supply Chain Manager"],
    avgStartSalaryMAD: 7000,
    avgMidSalaryMAD: 16000,
    topEmployers: ["Tanger Med", "Renault Tanger", "SNPT", "DHL Maroc", "Maersk Maroc", "Free Zones Tanger", "automotive suppliers"],
    employmentRate: 90,
    internationalOpportunities: true,
  },
  "ensa-tetouan": {
    jobFamilies: ["Ingénieur informatique", "Ingénieur télécoms", "Développeur web/mobile", "Ingénieur réseaux", "Chargé IT"],
    avgStartSalaryMAD: 6000,
    avgMidSalaryMAD: 14000,
    topEmployers: ["PME Tétouan", "Maroc Telecom régional", "startups nord", "Tanger Med support", "bureaux d'études"],
    employmentRate: 86,
    internationalOpportunities: false,
  },
  "ensa-oujda": {
    jobFamilies: ["Ingénieur génie civil", "Ingénieur électrique", "Ingénieur informatique", "Chef de projet", "Ingénieur mines (oriental)"],
    avgStartSalaryMAD: 6000,
    avgMidSalaryMAD: 13000,
    topEmployers: ["PME Oujda", "ONEE oriental", "Bureau d'études Oujda", "Jerada mining", "construction régionale"],
    employmentRate: 85,
    internationalOpportunities: false,
  },
  "ensa-beni-mellal": {
    jobFamilies: ["Ingénieur génie civil", "Ingénieur électrique", "Ingénieur informatique", "Chef de projet", "Ingénieur agroalimentaire"],
    avgStartSalaryMAD: 6000,
    avgMidSalaryMAD: 13000,
    topEmployers: ["OCP Group", "ONEE", "PME Béni Mellal-Khénifra", "agro-industrie régionale", "construction"],
    employmentRate: 85,
    internationalOpportunities: false,
  },
  "ensa-el-jadida": {
    jobFamilies: ["Ingénieur génie civil", "Ingénieur électrique", "Ingénieur agroalimentaire", "Développeur logiciel", "Chef de projet portuaire"],
    avgStartSalaryMAD: 6000,
    avgMidSalaryMAD: 13000,
    topEmployers: ["Port El Jadida", "OCP Jorf Lasfar", "agro-industrie Doukkala", "PME El Jadida", "construction"],
    employmentRate: 85,
    internationalOpportunities: false,
  },
  "ensa-berrechid": {
    jobFamilies: ["Ingénieur génie civil", "Ingénieur électrique", "Ingénieur informatique", "Chef de projet", "Ingénieur qualité"],
    avgStartSalaryMAD: 6000,
    avgMidSalaryMAD: 13000,
    topEmployers: ["PME Berrechid-Settat", "industrie Chaouia", "OCP", "construction régionale", "LEONI"],
    employmentRate: 84,
    internationalOpportunities: false,
  },
  "ensa-khouribga": {
    jobFamilies: ["Ingénieur informatique", "Ingénieur électrique", "Ingénieur minier", "Ingénieur génie civil", "Chef de projet OCP"],
    avgStartSalaryMAD: 6500,
    avgMidSalaryMAD: 14000,
    topEmployers: ["OCP Group", "OCP Digital", "PME Khouribga", "ONEE", "industrie phosphate"],
    employmentRate: 87,
    internationalOpportunities: false,
  },
  "ensa-safi": {
    jobFamilies: ["Ingénieur génie civil", "Ingénieur électrique", "Ingénieur chimie/phosphate", "Ingénieur environnement", "Chef de projet"],
    avgStartSalaryMAD: 6000,
    avgMidSalaryMAD: 13000,
    topEmployers: ["OCP Safi", "Groupe Alliances", "ONEE", "PME Safi", "industrie chimique"],
    employmentRate: 85,
    internationalOpportunities: false,
  },
  "ensa-al-hoceima": {
    jobFamilies: ["Ingénieur génie civil", "Ingénieur électrique", "Ingénieur informatique", "Chef de projet", "Ingénieur pêche"],
    avgStartSalaryMAD: 5500,
    avgMidSalaryMAD: 12000,
    topEmployers: ["PME Al Hoceima", "ONEE régional", "port Al Hoceima", "construction Rif", "secteur pêche"],
    employmentRate: 82,
    internationalOpportunities: false,
  },

  // ─── ELITE BUSINESS — ENCG NETWORK ────────────────────────────────────────
  "encg-casablanca": {
    jobFamilies: ["Contrôleur de gestion", "Responsable marketing", "Commercial B2B", "Responsable RH", "Analyste financier", "Category Manager"],
    avgStartSalaryMAD: 7500,
    avgMidSalaryMAD: 18000,
    topEmployers: ["Attijariwafa Bank", "OCP", "L'Oréal Maroc", "P&G Maroc", "Nestlé Maroc", "Unilever", "Danone", "Marjane"],
    employmentRate: 92,
    internationalOpportunities: false,
    "keyInsight": "La meilleure ENCG du réseau. Forte insertion dans les FMCG (L'Oréal, P&G, Nestlé, Unilever) et le secteur bancaire. Marketing et finance sont les filières les plus compétitives."
  },

  "encg-agadir": {
    jobFamilies: ["Manager commercial", "Responsable marketing", "Gestionnaire PME", "Contrôleur de gestion", "Chargé export", "Agro-business Manager"],
    avgStartSalaryMAD: 6500,
    avgMidSalaryMAD: 15000,
    topEmployers: ["Groupe Zniber", "COSUMAR", "ONCF", "Souss-Massa invest.", "Agroconcept", "Lesieur Cristal", "Sidi Ali"],
    employmentRate: 88,
    internationalOpportunities: false,
  },
  "encg-fes": {
    jobFamilies: ["Manager commercial", "Contrôleur de gestion", "Responsable RH", "Analyste financier", "Chef de projet", "Supply Chain Manager"],
    avgStartSalaryMAD: 6500,
    avgMidSalaryMAD: 15000,
    topEmployers: ["BMCE Bank", "CIH Bank", "Renault Maroc", "Maroc Telecom", "Groupe Holcim", "Fès-Meknès regional investors"],
    employmentRate: 87,
    internationalOpportunities: false,
  },
  "encg-tanger": {
    jobFamilies: ["Manager logistique", "Responsable supply chain", "Chargé export", "Analyste financier", "Commercial B2B"],
    avgStartSalaryMAD: 7000,
    avgMidSalaryMAD: 16000,
    topEmployers: ["Tanger Med", "Renault Tanger", "Maersk Maroc", "DHL Maroc", "SNPT", "Free Zones Tanger"],
    employmentRate: 89,
    internationalOpportunities: true,
  },
  "encg-marrakech": {
    jobFamilies: ["Manager commercial", "Responsable marketing", "Gestionnaire hôtelier", "Contrôleur de gestion", "Entrepreneur tourism"],
    avgStartSalaryMAD: 6500,
    avgMidSalaryMAD: 15000,
    topEmployers: ["Groupe AccorHotels", "Royal Air Maroc", "ONMT", "agences de voyage", "PME tourism Marrakech", "Palmeraie Development"],
    employmentRate: 86,
    internationalOpportunities: false,
  },
  "encg-oujda": {
    jobFamilies: ["Manager commercial", "Responsable RH", "Analyste financier", "Chargé export (Algérie)", "Entrepreneur"],
    avgStartSalaryMAD: 6000,
    avgMidSalaryMAD: 14000,
    topEmployers: ["BMCE Bank", "CIH Bank", "Oujda regional investors", "PME Oriental", "Comptoirs Miniers"],
    employmentRate: 85,
    internationalOpportunities: false,
  },
  "encg-settat": {
    jobFamilies: ["Manager commercial", "Contrôleur de gestion", "Responsable RH", "Gestionnaire PME", "Chargé export"],
    avgStartSalaryMAD: 6000,
    avgMidSalaryMAD: 13000,
    topEmployers: ["PME Settat region", "OCP Khouribga", "Groupe Holcim", "Attijariwafa Bank régional"],
    employmentRate: 85,
    internationalOpportunities: false,
  },
  "encg-kenitra": {
    jobFamilies: ["Manager commercial", "Responsable RH", "Analyste financier", "Gestionnaire PME", "Chargé communication"],
    avgStartSalaryMAD: 6000,
    avgMidSalaryMAD: 14000,
    topEmployers: ["PSA Kénitra", "Renault Maroc", "SNEP", "LEONI", "Safran Maroc", "ONCF", "Rabat-Salé-Kénitra regional"],
    employmentRate: 87,
    internationalOpportunities: false,
  },
  "encg-el-jadida": {
    jobFamilies: ["Manager commercial", "Responsable marketing", "Gestionnaire PME", "Chargé export", "Tourism Manager"],
    avgStartSalaryMAD: 5800,
    avgMidSalaryMAD: 13000,
    topEmployers: ["PME El Jadida", "tourism operators", "port logistics", "Attijariwafa Bank régional"],
    employmentRate: 84,
    internationalOpportunities: false,
  },
  "encg-beni-mellal": {
    jobFamilies: ["Manager commercial", "Responsable RH", "Gestionnaire PME", "Chargé export", "Agro-business Manager"],
    avgStartSalaryMAD: 5800,
    avgMidSalaryMAD: 13000,
    topEmployers: ["PME Béni Mellal-Khénifra", "OCP", "Groupe Holcim", "Cosumar", "agro-industrie régionale"],
    employmentRate: 84,
    internationalOpportunities: false,
  },
  "encg-dakhla": {
    jobFamilies: ["Manager commercial", "Responsable tourisme", "Chargé export pêche", "Gestionnaire PME", "Responsable logistique"],
    avgStartSalaryMAD: 6000,
    avgMidSalaryMAD: 13000,
    topEmployers: ["Office National des Pêches", "ONMT Dakhla", "operators touristiques", "PME provinces du Sud"],
    employmentRate: 83,
    internationalOpportunities: false,
  },

  // ─── MEDICINE & PHARMACY (FM) ─────────────────────────────────────────────
  "fm-rabat": {
    jobFamilies: ["Médecin généraliste", "Spécialiste hospitalier", "Chirurgien", "Pharmacien", "Chercheur médical", "Médecin légiste"],
    avgStartSalaryMAD: 7500,
    avgMidSalaryMAD: 40000,
    topEmployers: ["CHU Ibn Sina", "Cliniques privées", "Ministère de la Santé", "OCP", "CNSS", "Hôpitaux militaires", "Recherche internationale"],
    employmentRate: 99,
    internationalOpportunities: true,
    "keyInsight": "Première faculté de médecine du Maroc. Les diplômés dominent les postes de spécialistes à Rabat et Casablanca. La migration internationale (France, Belgique, Golfe) est courante pour les médecins marocains."
  },

  "fm-casablanca": {
    jobFamilies: ["Médecin généraliste", "Spécialiste hospitalier", "Chirurgien", "Dentiste", "Pharmacien", "Biologiste médical"],
    avgStartSalaryMAD: 7500,
    avgMidSalaryMAD: 40000,
    topEmployers: ["CHU Ibn Rochd", "Clinique Casablanca", "Polyclinique privée", "Ministère Santé", "CNOPS", "Pharma industry"],
    employmentRate: 99,
    internationalOpportunities: true,
    "keyInsight": "Plus grande faculté de médecine par volume. Riche écosystème de cliniques privées à Casablanca. Un spécialiste établi en secteur privé peut atteindre 50 000+ MAD/mois."
  },

  "fm-fes": {
    jobFamilies: ["Médecin généraliste", "Spécialiste", "Chirurgien", "Médecin urgentiste", "Pharmacien", "Biologiste"],
    avgStartSalaryMAD: 7500,
    avgMidSalaryMAD: 38000,
    topEmployers: ["CHU Hassan II Fès", "Cliniques privées", "Ministère de la Santé", "OCP Health", "Pharma régional"],
    employmentRate: 99,
    internationalOpportunities: true,
  },
  "fm-marrakech": {
    jobFamilies: ["Médecin généraliste", "Spécialiste", "Chirurgien", "Médecin de famille", "Pharmacien", "Biologiste"],
    avgStartSalaryMAD: 7500,
    avgMidSalaryMAD: 38000,
    topEmployers: ["CHU Mohammed VI", "Clinique Internationale Marrakech", "Ministère Santé", "tourism health services", "cliniques privées"],
    employmentRate: 99,
    internationalOpportunities: true,
  },
  "fm-oujda": {
    jobFamilies: ["Médecin généraliste", "Médecin urgentiste", "Pharmacien", "Chirurgien", "Médecin de famille"],
    avgStartSalaryMAD: 7000,
    avgMidSalaryMAD: 35000,
    topEmployers: ["CHU Oujda", "Ministère Santé", "Cliniques privées Oujda", "CHU Oriental", "centres de santé régionaux"],
    employmentRate: 99,
    internationalOpportunities: false,
  },
  "fm-tanger": {
    jobFamilies: ["Médecin généraliste", "Spécialiste", "Pharmacien", "Chirurgien", "Médecin de famille"],
    avgStartSalaryMAD: 7000,
    avgMidSalaryMAD: 36000,
    topEmployers: ["CHU Tanger", "cliniques privées Tanger", "Ministère Santé", "Tanger Med health services", "hôpitaux régionaux"],
    employmentRate: 99,
    internationalOpportunities: true,
  },
  "fm-agadir": {
    jobFamilies: ["Médecin généraliste", "Spécialiste", "Pharmacien", "Chirurgien", "Médecin de famille", "Biologiste"],
    avgStartSalaryMAD: 7000,
    avgMidSalaryMAD: 35000,
    topEmployers: ["CHU Hassan II Agadir", "CHU Ibn Rochd Agadir", "cliniques privées Agadir", "Ministère Santé", "tourism health"],
    employmentRate: 99,
    internationalOpportunities: false,
  },

  // ─── DENTAL MEDICINE (FMD) ────────────────────────────────────────────────
  "fmd-rabat": {
    jobFamilies: ["Chirurgien-dentiste", "Orthodontiste", "Parodontiste", "Prothésiste", "Implantologiste", "Endodontiste"],
    avgStartSalaryMAD: 8000,
    avgMidSalaryMAD: 35000,
    topEmployers: ["CHU Ibn Sina", "cabinets privés Rabat", "polycliniques", "CHU militaire", "private dental chains"],
    employmentRate: 98,
    internationalOpportunities: true,
  },
  "fmd-casablanca": {
    jobFamilies: ["Chirurgien-dentiste", "Orthodontiste", "Parodontiste", "Prothésiste", "Implantologiste"],
    avgStartSalaryMAD: 8000,
    avgMidSalaryMAD: 35000,
    topEmployers: ["Cliniques dentaires Casablanca", "CHU Ibn Rochd", "cabinets privés", "polycliniques", "dental tourism international"],
    employmentRate: 98,
    internationalOpportunities: true,
  },
  "fmd-fes": {
    jobFamilies: ["Chirurgien-dentiste", "Orthodontiste", "Parodontiste", "Prothésiste"],
    avgStartSalaryMAD: 7500,
    avgMidSalaryMAD: 30000,
    topEmployers: ["CHU Hassan II Fès", "cabinets privés Fès", "cliniques régionales", "polycliniques"],
    employmentRate: 98,
    internationalOpportunities: false,
  },

  // ─── PARAMEDICAL (ISPITS) ─────────────────────────────────────────────────
  "ispits": {
    jobFamilies: ["Infirmier spécialisé", "Sage-femme", "Anesthésiste-réanimateur", "Radiologue manipuliste", "Kinésithérapeute", "Biologiste médical"],
    avgStartSalaryMAD: 5500,
    avgMidSalaryMAD: 12000,
    topEmployers: ["CHU régionaux", "cliniques privées", "Ministère de la Santé", "centres de santé", "laboratoires d'analyses", "centres de dialyse"],
    employmentRate: 95,
    internationalOpportunities: false,
  },

  // ─── ARCHITECTURE (ENA) ───────────────────────────────────────────────────
  "ena-rabat": {
    jobFamilies: ["Architecte DPLG", "Urbaniste", "Chef de projet immobilier", "Directeur artistique", "Architecte d'intérieur", "BIM Architect"],
    avgStartSalaryMAD: 6000,
    avgMidSalaryMAD: 20000,
    topEmployers: ["Al Omrane", "CGI", "TGCC", "Caisse de Dépôt et de Gestion", "Bureaux d'études", "promoteurs immobiliers"],
    employmentRate: 86,
    internationalOpportunities: true,
  },
  "ena-fes": {
    jobFamilies: ["Architecte DPLG", "Expert patrimoine", "Urbaniste", "Architecte de restauration", "Designer", "Architecte paysagiste"],
    avgStartSalaryMAD: 5500,
    avgMidSalaryMAD: 18000,
    topEmployers: ["Al Omrane", "Direction du Patrimoine", "UNESCO Maroc", "Bureaux d'études", "restauration médina Fès"],
    employmentRate: 83,
    internationalOpportunities: true,
  },
  "ena-marrakech": {
    jobFamilies: ["Architecte", "Designer d'intérieur", "Chef de projet hôtelier", "Urbaniste", "Architecte paysagiste", "Architecte tourisme"],
    avgStartSalaryMAD: 5500,
    avgMidSalaryMAD: 18000,
    topEmployers: ["Groupe AccorHotels", "Sefrioui Group", "TGCC", "Al Omrane", "Agences immobilières", "promoteurs riads", "hôtels de luxe"],
    employmentRate: 84,
    internationalOpportunities: true,
  },
  "ena-agadir": {
    jobFamilies: ["Architecte côtier", "Urbaniste", "Architecte tourisme", "Chef de projet", "Architecte paysagiste"],
    avgStartSalaryMAD: 5500,
    avgMidSalaryMAD: 17000,
    topEmployers: ["Al Omrane Agadir", "ONA immobilier", "Offices régionaux", "Hôtels Agadir", "promoteurs résidentiels", "resorts"],
    employmentRate: 83,
    internationalOpportunities: false,
  },
  "ena-oujda": {
    jobFamilies: ["Architecte", "Urbaniste", "Architecte industriel", "Chef de projet", "Expert immobilier"],
    avgStartSalaryMAD: 5000,
    avgMidSalaryMAD: 15000,
    topEmployers: ["Al Omrane Oriental", "Ministère de l'Habitat", "OCP Oujda", "Bureaux d'études", "PME construction oriental"],
    employmentRate: 81,
    internationalOpportunities: false,
  },
  "ena-tetouan": {
    jobFamilies: ["Architecte patrimoine", "Architecte hispano-marocain", "Urbaniste", "Chef de projet restauration", "Directeur artistique"],
    avgStartSalaryMAD: 5500,
    avgMidSalaryMAD: 16000,
    topEmployers: ["Inspection des Monuments", "UNESCO", "Al Omrane Nord", "Bureaux d'études Maroc-Espagne", "médina Tétouan"],
    employmentRate: 82,
    internationalOpportunities: true,
  },

  // ─── AGRICULTURE & VETERINAIRE ────────────────────────────────────────────
  "iav-hassan-ii": {
    jobFamilies: ["Ingénieur agronome", "Vétérinaire", "Ingénieur agroalimentaire", "Chercheur en agronomie", "Consultant agriculture", "Ingénieur environnement"],
    avgStartSalaryMAD: 8500,
    avgMidSalaryMAD: 22000,
    topEmployers: ["OCP Africa", "Al Moutmir", "FNADMV", "INRA Maroc", "FAO/ONU Maroc", "Ministère Agriculture", "ONSSA", "Cosumar", "Centrale Danone"],
    employmentRate: 92,
    internationalOpportunities: true,
    "keyInsight": "Seule école combinant agronomie et médecine vétérinaire au Maroc. Critique pour la sécurité alimentaire nationale. Les vétérinaires en pratique libérale gagnent 25 000+ MAD."
  },


  // ─── TOURISM ──────────────────────────────────────────────────────────────
  "isit-tanger": {
    jobFamilies: ["Manager hôtelier", "Revenue Manager", "Directeur d'agence de voyage", "Chargé tourisme durable", "Commercial hôtelier", "Event Manager"],
    avgStartSalaryMAD: 6000,
    avgMidSalaryMAD: 14000,
    topEmployers: ["Club Med", "Accor Hotels Maroc", "Royal Air Maroc", "ONMT", "agences de voyage", "tour operators", "resorts"],
    employmentRate: 87,
    internationalOpportunities: true,
  },

  // ─── CPGE ─────────────────────────────────────────────────────────────────
  // CPGE is a 2-year gateway program; careers depend on the CNC school reached.
  "cpge-moulay-youssef": {
    jobFamilies: ["Préparation CNC → Ingénieur (toutes spécialités)", "Normalien", "Professeur agrégé", "Chercheur"],
    avgStartSalaryMAD: 11000,
    avgMidSalaryMAD: 30000,
    topEmployers: ["EMI · ENSIAS · INPT · EHTP alumni employers (via CNC)", "OCP Group", "Capgemini", "Maroc Telecom", "CDG"],
    employmentRate: 90,
    internationalOpportunities: true,
  },
  "cpge-ferhat-hachad": {
    jobFamilies: ["Préparation CNC → Ingénieur", "Ingénieur industrial (STI)", "Chercheur", "Professeur agrégé"],
    avgStartSalaryMAD: 9000,
    avgMidSalaryMAD: 25000,
    topEmployers: ["ENSAM · ENSA alumni employers (via CNC)", "secteur automobile", "industrie", "PME technique"],
    employmentRate: 88,
    internationalOpportunities: false,
  },
  "cpge-alkindi-tanger": {
    jobFamilies: ["Préparation CNC → Ingénieur", "Ingénieur logistique", "Chercheur", "Professeur agrégé"],
    avgStartSalaryMAD: 9000,
    avgMidSalaryMAD: 25000,
    topEmployers: ["EMI · ENSIAS · EHTP alumni employers (via CNC)", "Tanger Med", "industrie nord"],
    employmentRate: 88,
    internationalOpportunities: false,
  },
  "cpge-ibn-youssef": {
    jobFamilies: ["Préparation CNC → Ingénieur", "Chercheur", "Professeur agrégé", "Ingénieur industrie"],
    avgStartSalaryMAD: 9000,
    avgMidSalaryMAD: 25000,
    topEmployers: ["EMI · ENSIAS · ENSA alumni employers (via CNC)", "industrie Marrakech", "secteur tourisme tech"],
    employmentRate: 87,
    internationalOpportunities: false,
  },
  "cpge-oujda": {
    jobFamilies: ["Préparation CNC → Ingénieur", "Chercheur", "Professeur agrégé"],
    avgStartSalaryMAD: 8500,
    avgMidSalaryMAD: 24000,
    topEmployers: ["ENSA · ENSAM alumni employers (via CNC)", "industrie oriental", "OCP"],
    employmentRate: 86,
    internationalOpportunities: false,
  },

  // ─── PRIVATE UNIVERSITIES ─────────────────────────────────────────────────
  "uir": {
    jobFamilies: ["Ingénieur logiciel", "Manager international", "Data Analyst", "Ingénieur aéronautique", "Chercheur", "Supply Chain Manager"],
    avgStartSalaryMAD: 10000,
    avgMidSalaryMAD: 30000,
    topEmployers: ["Boeing Maroc", "Airbus Africa", "OCP", "Capgemini", "Orange Digital Center", "Safran", "Thales", "Dassault Systèmes"],
    employmentRate: 90,
    internationalOpportunities: true,
    keyInsight: "Programmes bilingues/trilingues accrédités CTI. Forte filière aéronautique (partenariat Airbus). Tuition élevée mais ROI rapide via placement multinational. Partenariats de recherche avec MIT et Georgia Tech.",
  },
  "al-akhawayn": {
    jobFamilies: ["Manager international", "Diplomate", "Consultant", "Entrepreneur", "Chercheur", "Data Analyst", "Finance Analyst"],
    avgStartSalaryMAD: 12000,
    avgMidSalaryMAD: 38000,
    topEmployers: ["Deloitte", "BCG", "Ambassades", "Multinationales", "ONG internationales", "Banques internationales", "USAID", "World Bank"],
    employmentRate: 88,
    internationalOpportunities: true,
    keyInsight: "L'université américaine du Maroc — modèle liberal arts, 100% anglais. Les diplômés dominent le corps diplomatique, le conseil international et les organisations multilatérales. Bourses très généreuses (50–100% pour profils méritants).",
  },
  "hem": {
    jobFamilies: ["Manager marketing", "Directeur commercial", "Responsable RH", "Analyste financier", "Entrepreneur", "Consultant management"],
    avgStartSalaryMAD: 8500,
    avgMidSalaryMAD: 24000,
    topEmployers: ["Attijariwafa Bank", "Crédit du Maroc", "L'Oréal", "Total Maroc", "Philip Morris", "P&G", "Nestlé", "Unilever", "Groupe OCP"],
    employmentRate: 89,
    internationalOpportunities: false,
    keyInsight: "Première business school privée accréditée AACSB au Maroc. 12 000+ alumni dans les cercles dirigeants de Casablanca. Les FMCG (L'Oréal, P&G, Nestlé) recrutent massivement. Réseau alumni très actif.",
  },
  "mundiapolis": {
    jobFamilies: ["Manager international", "Ingénieur logiciel", "Juriste international", "Responsable RH", "Commercial export", "Compliance Officer"],
    avgStartSalaryMAD: 8000,
    avgMidSalaryMAD: 22000,
    topEmployers: ["Attijariwafa Bank", "ONA", "Groupe BMCE", "Entreprises Europe-Maroc", "ONG", "PME export", "startups"],
    employmentRate: 87,
    internationalOpportunities: true,
  },
  "emsi": {
    jobFamilies: ["Développeur web/mobile", "Ingénieur réseaux", "Cybersécurité", "Data Analyst", "Chef de projet IT", "DevOps"],
    avgStartSalaryMAD: 7000,
    avgMidSalaryMAD: 18000,
    topEmployers: ["CGI Maroc", "Capgemini", "Sqli", "IT Factory", "IBM Maroc", "startups Casa", "Orange Digital Center", "freelance/remote EU"],
    employmentRate: 88,
    internationalOpportunities: true,
  },
  "esith": {
    jobFamilies: ["Ingénieur textile", "Responsable qualité", "Manager production", "Ingénieur logistique", "Ingénieur procédés", "Fashion Tech"],
    avgStartSalaryMAD: 6500,
    avgMidSalaryMAD: 16000,
    topEmployers: ["ITHEMBA", "Smartex", "Industrie textile Casablanca", "Proline", "SETAVEX", "Inditex suppliers (Zara Maroc)", "H&M suppliers", "Groupe Adolfo Dominguez"],
    employmentRate: 87,
    internationalOpportunities: true,
  },
  "esca-casablanca": {
    jobFamilies: ["Manager stratégique", "Consultant", "Directeur marketing", "DG PME", "Directeur financier", "Business Developer"],
    avgStartSalaryMAD: 10000,
    avgMidSalaryMAD: 28000,
    topEmployers: ["Deloitte", "EY", "Multinationales France", "BMCE Bank", "Crédit Agricole", "BNP Paribas Maroc", "Société Générale", "Mazars"],
    employmentRate: 91,
    internationalOpportunities: true,
    keyInsight: "Grande école de management accréditée EFMD avec 150+ partenaires universitaires mondiaux. Conseil et finance sont les filières les plus fortes. Réseau français de corporate finance très dense.",
  },
  "upf": {
    jobFamilies: ["Médecin généraliste", "Pharmacien", "Juriste d'entreprise", "Manager", "Ingénieur logiciel", "Infirmier spécialisé"],
    avgStartSalaryMAD: 7000,
    avgMidSalaryMAD: 22000,
    topEmployers: ["Cliniques privées Fès", "CHU Hassan II", "cabinets d'avocats", "PME Fès-Meknès", "IT companies", "secteur santé"],
    employmentRate: 85,
    internationalOpportunities: false,
  },
  "upm": {
    jobFamilies: ["Médecin généraliste", "Pharmacien", "Ingénieur", "Manager", "Architecte", "Professionnel tourisme"],
    avgStartSalaryMAD: 7000,
    avgMidSalaryMAD: 22000,
    topEmployers: ["Cliniques privées Marrakech", "CHU Mohammed VI", "promoteurs immobiliers", "hôtels Marrakech", "PME"],
    employmentRate: 85,
    internationalOpportunities: false,
  },
  "um6ss": {
    jobFamilies: ["Médecin Clinique & Hospitalière", "Pharmacie & Industrie Pharmaceutique", "Chirurgie Dentaire", "Kinésithérapie & Rééducation", "Management Hospitalier & Santé Publique"],
    avgStartSalaryMAD: 8000,
    avgMidSalaryMAD: 28000,
    topEmployers: ["CHU Cheikh Khalifa", "Cliniques privées Casa", "industrie pharmaceutique", "cabinets spécialisés", "management hospitalier", "secteur santé international"],
    employmentRate: 90,
    internationalOpportunities: true,
  },
  "esisa": {
    jobFamilies: ["Développeur logiciel", "Systèmes d'Information", "Data Analyst", "Responsable SI", "Cybersécurité"],
    avgStartSalaryMAD: 6500,
    avgMidSalaryMAD: 15000,
    topEmployers: ["IT companies Fès", "PME tech nord", "CGI Maroc", "secteur bancaire Fès", "startups régionales"],
    employmentRate: 82,
    internationalOpportunities: false,
  },
  "supdeco": {
    jobFamilies: ["Manager commercial", "Responsable marketing", "Directeur financier", "Responsable RH", "Entrepreneur"],
    avgStartSalaryMAD: 7500,
    avgMidSalaryMAD: 18000,
    topEmployers: ["Attijariwafa Bank", "L'Oréal Maroc", "multinationales FMCG", "PME export", "bureaux de conseil"],
    employmentRate: 86,
    internationalOpportunities: false,
  },
  "ipes": {
    jobFamilies: ["Commercial", "Gestionnaire PME", "Juriste", "Comptable", "Chargé communication"],
    avgStartSalaryMAD: 5000,
    avgMidSalaryMAD: 10000,
    topEmployers: ["PME Casablanca/Rabat", "administration", "secteur commerce", "ONG", "secteur informel"],
    employmentRate: 78,
    internationalOpportunities: false,
  },

  // ─── ENGINEERING / TECHNICAL ──────────────────────────────────────────────
  "enset-mohammedia": {
    jobFamilies: ["Ingénieur électrique", "Ingénieur mécanique", "Ingénieur informatique", "Professeur technique", "Ingénieur R&D"],
    avgStartSalaryMAD: 7000,
    avgMidSalaryMAD: 16000,
    topEmployers: ["ONEE", "ministère de l'Éducation", "Hassan II University", "industrie Mohammedia", "PME tech", "Lydec"],
    employmentRate: 85,
    internationalOpportunities: false,
  },
  "fp-rabat": {
    jobFamilies: ["Ingénieur systèmes", "Ingénieur électronique", "Ingénieur informatique", "Data Engineer", "Chercheur"],
    avgStartSalaryMAD: 6500,
    avgMidSalaryMAD: 15000,
    topEmployers: ["Mohammed V University", "PME tech Rabat", "startups", "ONEE", "OCP Digital", "CGI"],
    employmentRate: 80,
    internationalOpportunities: false,
  },

  // ─── ARTS & COMMUNICATION ─────────────────────────────────────────────────
  "isic-rabat": {
    jobFamilies: ["Journaliste TV/Presse", "Chargé de communication", "Community manager", "Réalisateur", "Relations publiques", "Social Media Manager"],
    avgStartSalaryMAD: 5000,
    avgMidSalaryMAD: 12000,
    topEmployers: ["2M", "Medi1TV", "MAP (Agence Maroc Presse)", "Radio Maroc", "Agences RP", "ministères communication", "private media"],
    employmentRate: 80,
    internationalOpportunities: false,
  },
  "inba-tetouan": {
    jobFamilies: ["Artiste plasticien", "Designer graphique", "Photographe", "Professeur arts plastiques", "Directeur artistique", "Scénographe"],
    avgStartSalaryMAD: 4000,
    avgMidSalaryMAD: 9000,
    topEmployers: ["Ministère Culture", "agences créatives", "écoles d'arts", "médias", "galeries", "industrie cinématographique"],
    employmentRate: 72,
    internationalOpportunities: false,
  },
  "isadac": {
    jobFamilies: ["Comédien / Acteur", "Metteur en scène", "Animateur culturel", "Scénographe", "Directeur artistique", "Manager culturel"],
    avgStartSalaryMAD: 4000,
    avgMidSalaryMAD: 9500,
    topEmployers: ["Théâtres nationaux", "CCM Maroc", "2M / Medi1TV", "agences événementielles", "ONG culturelles", "festivals"],
    employmentRate: 70,
    internationalOpportunities: true,
  },
  "esav-marrakech": {
    jobFamilies: ["Réalisateur", "Directeur artistique", "Animateur 3D", "Photographe professionnel", "Designer graphique", "Motion Designer"],
    avgStartSalaryMAD: 4500,
    avgMidSalaryMAD: 11000,
    topEmployers: ["FIFM", "CCM (Centre Cinématographique Marocain)", "Agences créatives", "Publicité", "Production indépendante", "Netflix/Amazon (freelance)", "studios animation"],
    employmentRate: 75,
    internationalOpportunities: true,
  },

  // ─── PUBLIC UNIVERSITIES — FS / FSJES / ISTA ──────────────────────────────
  "fs-rabat": {
    jobFamilies: ["Enseignant chercheur", "Biologiste", "Chimiste industriel", "Data Analyst", "Informaticien", "Géologue"],
    avgStartSalaryMAD: 5000,
    avgMidSalaryMAD: 12000,
    topEmployers: ["Mohammed V University", "INRA", "CNRST", "laboratoires", "enseignement", "PME tech"],
    employmentRate: 72,
    internationalOpportunities: false,
  },
  "ista": {
    jobFamilies: ["Technicien spécialisé IT", "Technicien électronicien", "Comptable", "Gestionnaire", "Commercial"],
    avgStartSalaryMAD: 4500,
    avgMidSalaryMAD: 9000,
    topEmployers: ["PME industrie", "commerce & distribution", "secteur privé", "administration", "artisanat industriel"],
    employmentRate: 78,
    internationalOpportunities: false,
  },
  "fsjes-casablanca": {
    jobFamilies: ["Avocat", "Magistrat", "Juriste d'entreprise", "Économiste", "Fonctionnaire", "Notaire", "Huissier", "Compliance Officer"],
    avgStartSalaryMAD: 5000,
    avgMidSalaryMAD: 12000,
    topEmployers: ["Ministères", "Cabinets d'avocats", "Banques", "Greffes", "Entreprises privées", "Notaires", "ONG", "tribunaux"],
    employmentRate: 70,
    internationalOpportunities: false,
  },
  "fsjes-agdal": {
    jobFamilies: ["Magistrat", "Diplomate", "Haut fonctionnaire", "Économiste politique", "Juriste international", "Chercheur politique"],
    avgStartSalaryMAD: 5500,
    avgMidSalaryMAD: 14000,
    topEmployers: ["Parlement", "Ministère des AE", "Ministère des Finances", "Magistrature", "ONU", "Ambassades", "Banque Mondiale", "IMF"],
    employmentRate: 72,
    internationalOpportunities: true,
  },
  "fsjes-marrakech": {
    jobFamilies: ["Juriste", "Comptable", "Gestionnaire", "Fonctionnaire", "Éducateur", "Assistant social"],
    avgStartSalaryMAD: 4500,
    avgMidSalaryMAD: 10000,
    topEmployers: ["Administration locale", "Banques Marrakech", "PME", "Enseignement", "ONG", "tribunaux régionaux"],
    employmentRate: 68,
    internationalOpportunities: false,
  },
  "fsjes-agadir": {
    jobFamilies: ["Juriste", "Économiste", "Gestionnaire", "Fonctionnaire", "Notaire", "Commercial export"],
    avgStartSalaryMAD: 4500,
    avgMidSalaryMAD: 10000,
    topEmployers: ["Administration régionale Agadir", "banques", "PME", "secteur tourisme", "notaires", "ONG"],
    employmentRate: 67,
    internationalOpportunities: false,
  },
  "fsjes-fes": {
    jobFamilies: ["Juriste", "Économiste", "Gestionnaire", "Fonctionnaire", "Droit islamique", "Enseignant"],
    avgStartSalaryMAD: 4500,
    avgMidSalaryMAD: 10000,
    topEmployers: ["Administration Fès", "tribunaux", "cabinets d'avocats", "banques", "enseignement"],
    employmentRate: 68,
    internationalOpportunities: false,
  },

  // ─── FST NETWORK ──────────────────────────────────────────────────────────
  "fst-mohammedia": {
    jobFamilies: ["Ingénieur logiciel", "Physicien industriel", "Chimiste industriel", "Chargé de formation", "Professeur chercheur", "Data Analyst"],
    avgStartSalaryMAD: 5500,
    avgMidSalaryMAD: 13000,
    topEmployers: ["ONEE", "Petromin", "ONA", "Enseignement supérieur", "Recherche", "PME industrie Mohammedia"],
    employmentRate: 75,
    internationalOpportunities: false,
  },
  "fst-fes": {
    jobFamilies: ["Technicien de recherche", "Enseignant chercheur", "Ingénieur d'étude", "Analyste chimiste", "Informaticien", "Data Scientist"],
    avgStartSalaryMAD: 5000,
    avgMidSalaryMAD: 12000,
    topEmployers: ["Université SMSB", "INRA", "Industrie régionale", "Recherche publique", "PME tech Fès"],
    employmentRate: 72,
    internationalOpportunities: false,
  },
  "fst-marrakech": {
    jobFamilies: ["Informaticien", "Chimiste", "Ingénieur électrique", "Enseignant", "Technicien supérieur"],
    avgStartSalaryMAD: 4500,
    avgMidSalaryMAD: 11000,
    topEmployers: ["Université Cadi Ayyad", "INRA", "ONEE", "PME Marrakech", "enseignement secondaire"],
    employmentRate: 70,
    internationalOpportunities: false,
  },
  "fst-agadir": {
    jobFamilies: ["Informaticien", "Physicien", "Chimiste", "Biologiste marin", "Géologue", "Data Analyst"],
    avgStartSalaryMAD: 4500,
    avgMidSalaryMAD: 11000,
    topEmployers: ["Université Ibn Zohr", "INRA", "ONEE", "PME Agadir", "secteur pêche", "recherche marine"],
    employmentRate: 70,
    internationalOpportunities: false,
  },
  "fst-beni-mellal": {
    jobFamilies: ["Mathématicien", "Physicien", "Chimiste", "Biologiste", "Informaticien"],
    avgStartSalaryMAD: 4500,
    avgMidSalaryMAD: 11000,
    topEmployers: ["Université Sultan Moulay Slimane", "OCP", "enseignement", "PME Béni Mellal"],
    employmentRate: 68,
    internationalOpportunities: false,
  },
  "fst-tanger": {
    jobFamilies: ["Informaticien", "Ingénieur civil", "Ingénieur électrique", "Mathématicien", "Logisticien"],
    avgStartSalaryMAD: 5000,
    avgMidSalaryMAD: 12000,
    topEmployers: ["Université Abdelmalek Essaâdi", "Tanger Med", "ONEE", "PME nord", "secteur logistique"],
    employmentRate: 72,
    internationalOpportunities: false,
  },
  "fst-settat": {
    jobFamilies: ["Mathématicien", "Chimiste", "Informaticien", "Physicien", "Ingénieur civil"],
    avgStartSalaryMAD: 4500,
    avgMidSalaryMAD: 11000,
    topEmployers: ["Université Hassan Ier", "PME Settat", "OCP", "enseignement", "industrie Chaouia"],
    employmentRate: 68,
    internationalOpportunities: false,
  },
  "fst-errachidia": {
    jobFamilies: ["Mathématicien", "Physicien", "Chimiste", "Biologiste", "Géologue"],
    avgStartSalaryMAD: 4500,
    avgMidSalaryMAD: 10000,
    topEmployers: ["Université Moulay Ismaïl", "enseignement", "recherche publique", "PME Drâa-Tafilalet"],
    employmentRate: 65,
    internationalOpportunities: false,
  },
  "fst-al-hoceima": {
    jobFamilies: ["Mathématicien", "Physicien", "Chimiste", "Biologiste", "Informaticien"],
    avgStartSalaryMAD: 4500,
    avgMidSalaryMAD: 10000,
    topEmployers: ["Université Abdelmalek Essaâdi", "enseignement", "PME Al Hoceima", "secteur pêche"],
    employmentRate: 65,
    internationalOpportunities: false,
  },

  // ─── EST NETWORK ──────────────────────────────────────────────────────────
  "est-casablanca": {
    jobFamilies: ["Technicien supérieur IT", "Technicien commercial", "Assistant manager", "Technicien qualité", "Gestionnaire PME", "Développeur web"],
    avgStartSalaryMAD: 5000,
    avgMidSalaryMAD: 10000,
    topEmployers: ["PME industrie", "Commerce & distribution", "ONDA", "ONA", "Secteur privé", "startups Casa"],
    employmentRate: 83,
    internationalOpportunities: false,
  },
  "est-sale": {
    jobFamilies: ["Technicien supérieur", "Assistant manager", "Technicien commercial", "Gestionnaire", "Technicien BTP"],
    avgStartSalaryMAD: 4800,
    avgMidSalaryMAD: 9500,
    topEmployers: ["Administration publique", "Commerce Rabat-Salé", "PME", "ONCF", "ONA", "secteur public"],
    employmentRate: 82,
    internationalOpportunities: false,
  },
  "est-fes": {
    jobFamilies: ["Technicien supérieur IT", "Technicien commercial", "Assistant manager", "Technicien qualité", "Gestionnaire"],
    avgStartSalaryMAD: 4500,
    avgMidSalaryMAD: 9500,
    topEmployers: ["PME Fès", "administration régionale", "industrie locale", "banques Fès", "commerce"],
    employmentRate: 81,
    internationalOpportunities: false,
  },
  "est-meknes": {
    jobFamilies: ["Technicien industriel", "Gestionnaire maintenance", "Informaticien de gestion", "Commercial", "Technicien qualité"],
    avgStartSalaryMAD: 4500,
    avgMidSalaryMAD: 9500,
    topEmployers: ["industrie Meknès", "ONCF", "agriculture", "PME Fès-Meknès", "Stellantis"],
    employmentRate: 81,
    internationalOpportunities: false,
  },
  "est-agadir": {
    jobFamilies: ["Technicien tourisme & hôtellerie", "Technicien pêche", "Responsable commercial", "Gestionnaire hôtelier", "Agent import-export"],
    avgStartSalaryMAD: 5000,
    avgMidSalaryMAD: 10000,
    topEmployers: ["Clubs Med Agadir", "Sofitel Agadir", "Port d'Agadir", "Office Halieutique", "ONMT", "agro-industrie Souss"],
    employmentRate: 85,
    internationalOpportunities: true,
  },
  "est-oujda": {
    jobFamilies: ["Technicien supérieur", "Commercial", "Assistant manager", "Technicien électronique", "Gestionnaire PME"],
    avgStartSalaryMAD: 4500,
    avgMidSalaryMAD: 9000,
    topEmployers: ["PME Oriental", "administration Oujda", "commerce régional", "industrie locale", "ONG"],
    employmentRate: 79,
    internationalOpportunities: false,
  },

  // ─── MISC ─────────────────────────────────────────────────────────────────
  "cfg-schools": {
    jobFamilies: ["Trader", "Analyste financier", "Gestionnaire de portefeuille", "Risk Manager", "FinTech Product Manager", "Compliance Officer"],
    avgStartSalaryMAD: 9000,
    avgMidSalaryMAD: 28000,
    topEmployers: ["CFG Bank", "Valoris Securities", "Bourse de Casablanca", "BMCE Capital", "Upline", "Attijariwafa Corporate", "CDG Capital"],
    employmentRate: 90,
    internationalOpportunities: false,
  },
};

export function getSchoolCareers(slug: string): SchoolCareers | undefined {
  return CAREERS_DATA[slug];
}
